import { useMemo } from 'react'
import Debug from 'debug'
import { sleep } from 'radash'
import type { Interceptor } from '@connectrpc/connect'
import { Code, ConnectError } from '@connectrpc/connect'
import { createGrpcWebTransport } from '@connectrpc/connect-web'

import { getOrRefreshAccessToken } from '@jotta/auth-client/useAuthStatus'
import { env } from '@jotta/utils/env'
import { enrichError } from '@jotta/utils/error'
import { readVersion } from '@jotta/utils/frontendbackend'
import { nextXid } from '@jotta/utils/xid'
import { resolveVisitorId } from '@jotta/utils/matomo'

const debug = Debug('grpc:transport')

let userAgent: string | undefined

function getUserAgent() {
  userAgent ??= `web/${readVersion() ?? `unknown-${env.env}`} ${navigator.userAgent}`
  return userAgent
}

declare global {
  interface Window {
    __CONNECT_WEB_DEVTOOLS__: Interceptor | undefined
  }
}

export const devToolsInterceptor: Interceptor = next => {
  const connectwebdevtools = window.__CONNECT_WEB_DEVTOOLS__
  return connectwebdevtools ? connectwebdevtools(next) : req => next(req)
}

export const logInterceptor: (name: string, xid?: string) => Interceptor =
  (transportName, anXid) => next => async req => {
    const xid = anXid || nextXid()
    const methodName = req.method.name
    const log = debug.extend(transportName).extend(methodName).extend(xid)
    try {
      const signal = req.signal
      signal.addEventListener('abort', () =>
        // the built-in timeout signaling produces just a basic "the operation timed out" error message
        // for streaming operations. Let's add some more context to the error message.
        enrichError(signal.reason, xid, methodName, transportName),
      )
      req.header.set('x-id', xid)
      req.header.set('x-user-agent', getUserAgent())
      log('Request', req)
      const res = await next(req)
      log('Response', res)
      return res
    } catch (error) {
      enrichError(error, xid, methodName, transportName)
      if (error instanceof ConnectError) {
        const { code } = error
        const codeName = Code[code]
        log('%s - code "%s" message "%s"', error.name, codeName, error.message)
      } else if (error instanceof Error) {
        log('%s: message: %s', error.name, error.message, error)
      } else {
        log('Unknown error', error)
      }
      throw error
    }
  }

export const authInterceptor: (
  tokenResolver: () => Promise<string>,
) => Interceptor = tokenResolver => next => async req => {
  const log = debug.extend(req.method.name)
  const token = await tokenResolver()

  if (token) {
    req.header.set('Authorization', `Bearer ${token}`)
  } else {
    log('Token not found, performing request without authorization')
  }

  return next(req)
}

const trackingInterceptor: Interceptor = next => async req => {
  const visitorId = resolveVisitorId()
  if (visitorId) {
    req.header.set('x-vid', visitorId)
  }
  return next(req)
}

/**
 * Slow down requests
 *
 * Only use for testing loading states during development!
 */
export const slowInterceptor: Interceptor = next => async req => {
  await sleep(500)
  return next(req)
}

export const publicTransport = createGrpcWebTransport({
  baseUrl: env.grpcApi,
  defaultTimeoutMs: 10000,
  interceptors: [
    devToolsInterceptor,
    logInterceptor('publicTransport'),
    trackingInterceptor,
  ],
})
export const createAuthTransport = ({
  timeoutMs = 10_000,
  tokenResolver = getOrRefreshAccessToken,
  xid,
}: {
  timeoutMs?: number
  tokenResolver?: () => Promise<string>
  xid?: string
}) =>
  createGrpcWebTransport({
    baseUrl: env.grpcApi,
    defaultTimeoutMs: timeoutMs,
    interceptors: [
      devToolsInterceptor,
      logInterceptor('authTransport', xid),
      authInterceptor(tokenResolver),
      trackingInterceptor,
    ],
  })
export const authTransport = createAuthTransport({})

export function useAuthTransport({
  timeoutMs,
  token,
}: {
  timeoutMs?: number
  token?: string
}) {
  return useMemo(
    () =>
      createAuthTransport({
        timeoutMs,
        tokenResolver: token ? async () => token : undefined,
      }),
    [timeoutMs, token],
  )
}
